import React, {useEffect, useRef, useState} from 'react'
import {useFormik} from 'formik'
import {KTSVG} from '../../../_metronic/helpers'
import {
  createCommentaireClientDemande, createMessTelevendeur,
  getCommentaireClientDemandeByClientDemandeId,
} from '../../../components/requests'
import {ModeleCommentaireClientDemande} from '../../../components/Models'
import {useAuth} from '../../modules/auth'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {LoadingData} from '../../../components/LoadingData'

interface Props {
  idClientDemande: number | string | undefined
  clientId?: number | string | null
}

const CommentaireClientDemande: React.FC<Props> = ({idClientDemande, clientId} : Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [commentaires, setCommentaires] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [commentBtnOnly, setCommentBtnOnly] = useState<boolean>(true)
  const [isFirstOpen, setIsFirstOpen] = useState<boolean>(true)
  const eventSourceRef = useRef<EventSource | null>(null)

  const {currentUser} = useAuth()
  const endRef = useRef<HTMLDivElement | null>(null)

  const toggleChat = () => setIsOpen((prev) => !prev)

  // Récupération des données (UNIQUEMENT première ouverture)
  const getCommentaires = async () => {
    if (!idClientDemande) return
    try {
      setLoading(true)
      const res = await getCommentaireClientDemandeByClientDemandeId(idClientDemande)
      if (res?.status === 200 && res.data) {
        setCommentaires(res.data)
      }
    } catch (error) {
      console.error('Erreur chargement commentaires', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!idClientDemande || eventSourceRef.current) return

    const clientDemandeIdNum = Number(idClientDemande)

    const baseMercureUrl = process.env.REACT_APP_MERCURE_URL
    const mercureUrl = `${baseMercureUrl}/.well-known/mercure?topic=logs/clientdemande/${clientDemandeIdNum}`

    eventSourceRef.current = new EventSource(mercureUrl)

    eventSourceRef.current.onmessage = (e) => {
      const data = JSON.parse(e.data)
      if (data.clientDemandeId == clientDemandeIdNum) {
        // On ajoute les changements
        setCommentaires((prevComment) => [...prevComment, data.logs[0]])
        if (isOpen) endRef.current?.scrollIntoView({behavior: 'smooth'})
      }
    }

    eventSourceRef.current.onerror = () => {
      setTimeout(() => {
        if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
          eventSourceRef.current?.close()
          eventSourceRef.current = null
        }
      }, 3000)
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [idClientDemande])

  useEffect(() => {
    if (isOpen && idClientDemande && isFirstOpen) {
      getCommentaires()
      setIsFirstOpen(false)
    }
  }, [isOpen, idClientDemande, isFirstOpen])

  // Gestion du SCROLL
  useEffect(() => {
    if (isOpen && !loading && commentaires.length > 0) {
      const timer = setTimeout(() => {
        endRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'})
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [commentaires, isOpen, loading])

  // Gestion de la position du bouton
  useEffect(() => {
    const body = document.body
    const updateBtnPos = () => {
      setCommentBtnOnly(!body.hasAttribute('data-kt-scrolltop'))
    }

    const observer = new MutationObserver(updateBtnPos)
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['data-kt-scrolltop'],
    })

    updateBtnPos()
    return () => observer.disconnect()
  }, [])

  const formik = useFormik({
    initialValues: {message: ''},
    onSubmit: async (values, {resetForm}) => {
      if (!values.message.trim()) return

      const nouveauCommentaire: ModeleCommentaireClientDemande = {
        idClientDemande,
        message: values.message.trim(),
        description: values.message.trim(),
        auteur: currentUser?.nom ?? currentUser?.pseudo,
      }

      try {
        await createCommentaireClientDemande(nouveauCommentaire)
        resetForm()
      } catch (err) {
        console.error("Erreur lors de l'envoi du commentaire", err)
        if (isFirstOpen) await getCommentaires()
      }
    },
  })

  // Pour poster les messages télévendeurs
  const handlePostMessTV = async () => {
    try {
      await createMessTelevendeur(idClientDemande, clientId, formik.values.message)
      formik.setFieldValue('message', '')
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <>
      {isOpen && (
        <div
          className='card shadow-lg position-fixed'
          style={{
            bottom: '0px',
            right: '20px',
            width: '620px',
            height: '820px',
            zIndex: 106, //105 pour l'icône scrolltoptop
          }}
        >
          <div className='card-header d-flex justify-content-between align-items-center px-5 min-h-55px'>
            <h6 className='mb-0'>Fil des modifications</h6>
            <button className='btn btn-sm btn-icon btn-light' onClick={toggleChat}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
            </button>
          </div>

          <div className='card-body overflow-auto px-5' style={{flex: 1}}>
            {loading && commentaires.length === 0 ? (
              <LoadingData />
            ) : commentaires.length === 0 ? (
              <div className='text-muted d-flex justify-content-center align-items-center h-100'>
                Aucun commentaire pour le moment.
              </div>
            ) : (
              <>
                {commentaires.map((c, idx) => (
                  <div key={idx} className='d-flex mb-3'>
                    <div
                      className={`bg-light${
                        c.commentaire ? '-info' : ''
                      } rounded px-3 py-2 w-100 shadow-sm`}
                    >
                      <div
                        className='d-flex justify-content-between text-muted mb-1'
                        style={{fontSize: '0.75rem'}}
                      >
                        <strong>
                          {c.auteur ?? c.utilisateur?.nom ?? c.assigneeA?.nom ?? 'Système'}
                        </strong>
                        <span>
                          {c.entite && <span className='badge badge-info me-1'>Auto</span>}
                          {c.titre && <span className='badge badge-primary me-1'>Télévendeur</span>}
                          {(c.dateCreation || c.date || c.dateEcheance) &&
                            new Date(c.dateCreation ?? c.date ?? c.dateEcheance).toLocaleString(
                              'fr-FR',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              }
                            )}
                        </span>
                      </div>
                      <div style={{wordBreak: 'break-word'}}>{c.description ?? c.commentaire}</div>
                      <div className='text-end' style={{fontSize: '0.75rem'}}>
                        {c.statut?.id &&
                          <span
                            className={`badge badge-${c.statut?.libelle === 'Lu' ? 'primary' : 'danger'} me-1`}
                            style={{fontSize: '0.75rem'}}>{c.statut.libelle}
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                ))}
                {/* L'élément vers lequel on scroll */}
                <div ref={endRef} />
              </>
            )}
          </div>

          <div className='card-footer px-5 py-4'>
            <form onSubmit={formik.handleSubmit} className='d-flex align-items-center gap-2'>
              <textarea
                name='message'
                className='form-control form-control-flush border-1'
                placeholder='Écrire un commentaire...'
                value={formik.values.message}
                onChange={formik.handleChange}
              />
              <button
                type='submit'
                className='btn btn-icon btn-success'
                disabled={!formik.values.message.trim() || loading}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-2' />
              </button>
              <button
                type='button'
                className='btn btn-icon btn-success'
                data-tooltip-id='message-tooltip'
                data-tooltip-content='Message télévendeur'
                data-tooltip-place='left'
                title='Message télévendeur'
                disabled={!formik.values.message.trim() || loading}
                onClick={handlePostMessTV}
              >
                <KTSVG
                  path='/media/icons/duotune/communication/com011.svg'
                  className='svg-icon-2'
                />
              </button>
            </form>
          </div>
        </div>
      )}

      <OverlayTrigger
        placement='left'
        delay={{show: 250, hide: 400}}
        overlay={<Tooltip id='tooltip-commentaire'>Commentaires...</Tooltip>}
      >
        <button
          className='btn btn-icon btn-success shadow position-fixed'
          style={{
            bottom: commentBtnOnly ? '40px' : '100px', // Monte si le bouton scroll-top de Metronic est là
            right: '20px',
            width: '36px',
            height: '36px',
            padding: 0,
            transition: 'bottom 0.3s ease-in-out',
          }}
          onClick={toggleChat}
        >
          <KTSVG path='/media/icons/duotune/communication/com012.svg' className='svg-icon-1' />
        </button>
      </OverlayTrigger>
    </>
  )
}

export default CommentaireClientDemande
